import type { GuildInfo } from './game/guild.js';
import type { ThreatState } from './game/threat.js';
import type { PartyMember } from './game/party.js';
import type { Extras } from './game/extras.js';
import type { CharmState } from './game/charms.js';
import type { Bosstiary } from './game/bosses.js';
import type { BountyState } from './game/bounty.js';
import type { PreyState } from './game/prey.js';
// Formato das mensagens trocadas entre navegador e servidor.
import type { MarketHistoryRow, MarketOffer, MarketStats } from './game/market.js';
import type { BpState } from './game/battlepass.js';
import type { VocationId } from './game/vocations.js';
import type { ItemInstance, MoveTarget } from './game/items.js';
import type { Creature } from './game/world.js';
import type { HelperSettings } from './game/helper.js';

export type Sex = 'male' | 'female';

export interface Outfit {
  lookType: number;
  head: number;
  body: number;
  legs: number;
  feet: number;
  addons: number;
  /** montaria em uso (lookType da montaria) */
  mount?: number;
}

export interface CharacterSummary {
  id: number;
  name: string;
  vocation: VocationId;
  sex: Sex;
  level: number;
  outfit: Outfit;
  /** 0 = sem promotion, 1 = promovido, 2 = segunda promotion */
  promotion: number;
}

export type SkillId = 'fist' | 'club' | 'sword' | 'axe' | 'distance' | 'shielding' | 'fishing';
export const SKILL_IDS: SkillId[] = ['fist', 'club', 'sword', 'axe', 'distance', 'shielding', 'fishing'];

export interface CharacterState extends CharacterSummary {
  experience: number;
  soul: number;
  speed: number;
  magicLevel: number;
  skills: Record<SkillId, number>;
  resets: number;
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  capacity: number;
}

// --- HTTP ---
export interface RegisterRequest { email: string; password: string; adult: boolean }
export interface LoginRequest { email: string; password: string }
export interface AuthResponse { token: string; email: string }
export interface CreateCharacterRequest { name: string; vocation: VocationId; sex: Sex }
export interface ApiError { error: string }

// --- WebSocket ---
export type ClientMessage =
  | { t: 'auth'; token: string; characterId: number }
  | { t: 'ping'; at: number }
  | { t: 'move'; itemId: number; to: MoveTarget; count?: number }
  | { t: 'step'; dir: number }
  | { t: 'turn'; dir: number }
  | { t: 'travel'; to: 'temple' | 'hunt'; hunt?: string; floor?: number }
  /** Liga/desliga a caça automática (o personagem procura e ataca monstros sozinho). */
  | { t: 'auto'; on: boolean }
  /** Escolhe um alvo (clique na criatura ou na Battle List). */
  | { t: 'attack'; id: string | null }
  | { t: 'look'; id: string }
  | { t: 'buy'; typeId: number; count: number }
  /** Vende os itens indicados; lista vazia = todo o loot comum vendável carregado. */
  | { t: 'sell'; itemIds: number[]; /** lista vazia: vende o loot até esta raridade (0 comum, 1 raro, 2 épico, 3 lendário) */ maxRarity?: number }
  | { t: 'helper'; settings: HelperSettings }
  | { t: 'deposit' }
  /** Usa algo da barra de ações (magia, runa ou poção). */
  | { t: 'use'; spell?: string; item?: number }
  /** Joga fora um item (some para sempre). */
  | { t: 'discard'; itemId: number }
  | { t: 'bless'; enhanced: boolean }
  /** escolhe o item de treino de presente (primeira entrada) */
  | { t: 'trainingGift'; name: string }
  /** usa um item de treino no boneco mais perto (ou para de treinar, sem itemId) */
  | { t: 'exercise'; itemId?: number }
  /** fala com um NPC (choice = opção escolhida na conversa) */
  | { t: 'npc'; id: string; choice?: string }
  /** puxa a alavanca de uma quest (estilo Annihilator) */
  | { t: 'lever'; x: number; y: number; f: number }
  /** compra na loja de donate (Noxara Coins); exercise = tipo do item de treino */
  | { t: 'storeBuy'; offer: string; exercise?: string; token?: boolean }
  /** troca de outfit, cores, addons e montaria (mount = id da montaria ou null) */
  | { t: 'outfit'; outfit: Outfit; mount: number | null }
  /** pega a recompensa diária (potion = tipo de poção; exercise = tipo do item de treino) */
  | { t: 'daily'; potion?: string; exercise?: string }
  /** passe de batalha: ver, pegar recompensa de um nível ou escolher a aura */
  | { t: 'bpass'; op: 'get' | 'claim' | 'aura'; track?: 'free' | 'prem'; level?: number; potion?: string; exercise?: string; aura?: string | null }
  /** prey: trocar lista, escolher, trocar bônus, qualquer criatura ou cancelar */
  | { t: 'prey'; op: 'get' | 'list' | 'pick' | 'bonus' | 'any' | 'cancel'; slot?: number; monster?: string }
  /** tarefas de caça: escolher, cancelar, trocar opções, comprar na loja de tarefas */
  | { t: 'bounty'; op: 'get' | 'pick' | 'cancel' | 'reroll' | 'buy' | 'talisman' | 'htpbuy' | 'deliver'; arg?: string }
  /** entra na sala de um boss (aba Bosses) */
  | { t: 'boss'; name: string; difficulty?: number }
  | { t: 'bossSlot'; slot: number; name: string | null }
  | { t: 'autoBoss'; op: 'set' | 'start' | 'stop'; list?: string[] }
  /** charms: liberar/melhorar, colocar numa criatura ou tirar */
  | { t: 'charm'; op: 'unlock' | 'assign' | 'remove' | 'card' | 'uncard'; id: string; monster?: string }
  /** refine +1 num equipamento (protect: usa pedra de proteção) */
  | { t: 'refine'; itemId: number; protect?: boolean }
  /** Coliseu: ver, entrar ou comprar na loja de marcas */
  | { t: 'colosseum'; op: 'get' | 'enter' | 'buy'; id?: string }
  /** party: convidar, aceitar, recusar, sair, expulsar */
  | { t: 'party'; op: 'get' | 'invite' | 'accept' | 'decline' | 'leave' | 'kick'; name?: string; id?: number }
  /** PvP: ver, entrar ou sair da fila de batalha */
  | { t: 'pvp'; op: 'get' | 'join' | 'leave' }
  /** ameaça mundial: ver ou entrar na arena final */
  | { t: 'threat'; op: 'get' | 'enter' }
  /** fendas (dungeons): ver ou entrar */
  | { t: 'rift'; op: 'get' | 'enter' }
  /** guild: criar, convidar, aceitar, recusar, sair, expulsar, promover, rebaixar, mensagem */
  | { t: 'guild'; op: 'get' | 'create' | 'invite' | 'accept' | 'decline' | 'leave' | 'kick' | 'promote' | 'demote' | 'motd'; arg?: string; id?: number }
  /** imbuement: aplicar (item, espaço, nome e grau, proteção) ou limpar um espaço */
  | { t: 'imbue'; op: 'apply' | 'clear'; itemId: number; slot: number; name?: string; tier?: number; protect?: boolean; tokens?: boolean }
  /** Exaltation Forge: fusão, transferência e conversões (dust → slivers → core) */
  | { t: 'forge'; op: 'fusion' | 'transfer' | 'slivers' | 'core'; a?: number; b?: number; core1?: boolean; core2?: boolean }
  /** salva a distribuição de pontos da Wheel of Destiny */
  | { t: 'wheel'; id: string; n: number }
  | { t: 'guildboss' }
  | { t: 'vipList'; op: 'get' | 'add' | 'remove'; name?: string }
  | { t: 'expedition'; op: 'get' | 'start' | 'collect' | 'reroll'; id?: number }
  | { t: 'offlineSkill'; skill: string | null }
  | { t: 'training' }
  /** quests clássicas: estado, entrar (com a party) e abrir o baú escolhido */
  | { t: 'conjure'; words: string }
  | { t: 'tower' | 'labyrinth' | 'wild'; op: 'get' | 'enter' }
  | { t: 'tutorial' }
  | { t: 'ferumbrasHat'; itemId: number }
  | { t: 'openBag'; itemId: number }
  | { t: 'soulpit'; itemId: number }
  | { t: 'house'; op: string; id?: number; arg?: string | number }
  | { t: 'fish'; op: 'start' | 'stop' }
  | { t: 'profSelect'; weaponId: number; level: number; index: number }
  | { t: 'achReq' }
  | { t: 'chat'; ch: import('./game/chat.js').ChatChannel; text: string; to?: string }
  | { t: 'quest'; op: 'get' | 'enter' | 'pick' | 'next'; id?: string; idx?: number }
  | { t: 'gem'; op: 'reveal' | 'socket' | 'unsocket' | 'destroy'; id: number }
  /** faz o reset do personagem */
  | { t: 'reset' }
  /** usa um item de domar num monstro perto (montarias domáveis) */
  | { t: 'tame'; itemId: number }
  /** paladin: escolhe a flecha ou o bolt equipado (de graça, cumprindo o level) */
  | { t: 'ammo'; typeId: number }
  /** mercado entre jogadores */
  | { t: 'market'; op: 'list' | 'sell' | 'sellCoins' | 'buy' | 'cancel' | 'createBuy' | 'sellTo' | 'history' | 'stats'; itemId?: number; offerId?: number; typeId?: number; count?: number; price?: number; anonymous?: boolean; currency?: 'gold' | 'coins' };

export type ServerMessage =
  | { t: 'welcome'; character: CharacterState; serverTime: number }
  | { t: 'pong'; at: number; serverTime: number }
  | { t: 'inventory'; items: ItemInstance[]; freeCapacity: number }
  | { t: 'notice'; kind: 'info' | 'error' | 'look'; text: string }
  /** recargas ativas: [chave, ms restantes] (magia pelas palavras, 'g:attack'/'g:healing'/'g:support', 'r:<runa>', 'heal:<palavras>', 'potion') */
  | { t: 'cds'; list: Array<[string, number]> }
  /** pede para o jogador escolher o item de treino de presente */
  | { t: 'trainingGift'; options: string[] }
  | { t: 'quests'; done: string[]; pick: string[] }
  | { t: 'questReward'; id: string }
  | { t: 'openWindow'; id: string }
  | { t: 'tower'; best: number; start: number }
  | { t: 'labyrinth'; left: number; total: number; tier: number; chestGold: number; gold: number }
  | { t: 'wild'; band: string; pool: string[]; online: number; left?: number }
  | { t: 'questNext'; name: string; stage: number; total: number }
  /** treinando com item de treino (null = parou) */
  | { t: 'exercise'; itemId: number | null; charges?: number }
  /** buffs ativos (until 0 = postura até trocar) e o que resta do Magic Shield */
  | { t: 'buffs'; list: Array<{ words: string; until: number }>; mshield: number; mshieldMax: number }
  | { t: 'kicked'; reason: string }
  /** outfits (nome → addons), montarias e auras que o personagem tem */
  | { t: 'cosmetics'; outfits: Record<string, number>; mounts: number[]; mount: number | null; auras: string[]; aura: string | null }
  /** ícones de condição do personagem (poison, burning, paralyzed, haste, battle, pz...) */
  | { t: 'flags'; list: string[] }
  /** passe de batalha do mês, auras ganhas e a aura em uso */
  | { t: 'bpass'; state: BpState; auras: string[]; aura: string | null; monsters?: Array<{ name: string; goal: number; level: number }> }
  /** recompensa diária: quantas já pegou no mês, se pode pegar hoje e até quando vale o boost de XP */
  | { t: 'daily'; claimed: number; canClaim: boolean; month: string; xpBoostUntil: number }
  /** prey do personagem (all = criaturas que dá para escolher com prey cards) */
  | { t: 'prey'; state: PreyState; rerollGold: number; all: string[] }
  /** tarefas de caça (info: abates pedidos, pontos e XP de cada criatura) */
  | { t: 'bounty'; state: BountyState; info: Record<string, { need: number; points: number; xp: number }>; rerollGold: number }
  /** bosstiário (abates e cooldowns) e o boss do dia */
  | { t: 'bosses'; list: Bosstiary; boosted: string | null; slots?: string[]; attempts?: { left: number; total: number; bought: number }; autoBoss?: { list: string[]; on: boolean; queue: string[]; cur?: string } }
  /** bestiário (abates por criatura) e charms */
  | { t: 'charms'; state: CharmState; kills: Record<string, number>; removeGold: number }
  /** Coliseu: tentativas, marcas, recorde, marcos e modificadores da temporada */
  | { t: 'colosseum'; left: number; total: number; bought: number; marks: number; best: number; milestones: number[]; mods: string[]; buyPrice: number }
  /** party: membros, convites recebidos e bônus de XP compartilhada */
  | { t: 'party'; members: PartyMember[]; invites: Array<{ id: number; name: string }>; bonus: number }
  /** PvP: fila, lutas de hoje, pontos e lutas do ciclo, fim do ciclo e se é Herói */
  | { t: 'pvp'; inQueue: boolean; today: number; points: number; fights: number; wins: number; cycleEnds: number; hero: boolean }
  /** ameaça mundial (null = nenhuma ativa; next = quando chega a próxima) */
  | { t: 'threat'; state: ThreatState | null; next: number; fallUntil?: number }
  /** fendas: entradas de hoje, tier do personagem, monstros do tier e gold da recompensa */
  | { t: 'rift'; left: number; total: number; tier: number; pool: string[]; gold: number; boss?: string }
  /** resultado dos giros da roleta */
  | { t: 'banner'; text: string; icon: number }
  /** Invasão de Noxara: ativa agora (até quando) e quando é a próxima */
  | { t: 'invasion'; active: boolean; ends: number; next: number; minLevel: number; maxLevel: number }
  | { t: 'prof'; state: Record<string, import('./game/proficiency.js').ProfState> }
  | { t: 'say'; id: string; text: string; color: number }
  | { t: 'ach'; done: Record<number, number>; progress: Record<number, [number, number]> }
  | { t: 'roulette'; prizes: Array<{ id: string; label: string; text: string; icon: number }> }
  /** guild do personagem, convites e o estado do cerco */
  | { t: 'guild'; guild: GuildInfo | null; rank: string | null; invites: Array<{ id: number; name: string }>; siege: Array<{ id: string; name: string; min: number; max: number; bonus: number; owner: string | null; top: Array<{ name: string; points: number }> }>; weekEnds: number; createGold: number; boss?: import('./game/guildboss.js').GuildBossView | null }
  | { t: 'vipList'; list: Array<{ name: string; level: number; vocation: string; online: boolean; where: string }>; max: number }
  | { t: 'fields'; list: number[][] }
  /** resultado de uma tentativa de refine (janela de Refine) */
  | { t: 'refineResult'; itemId: number; from: number; to: number; outcome: 'ok' | 'drop' | 'saved' | 'kept' }
  /** Coliseu: andar (onda) atual da tentativa; 0 = saiu */
  | { t: 'colWave'; floor: number }
  | { t: 'expeditions'; state: import('./game/expeditions.js').ExpeditionState; slots: number; rerollGold: number; vip?: boolean; est?: Record<number, { xp: number; gold: number }> }
  /** moedas e contadores extras (fragmentos, pedras, marcas do Coliseu, PvP) */
  | { t: 'extras'; extras: Extras }
  /** um abate a mais no bestiário */
  | { t: 'bkill'; name: string; kills: number }
  /** ofertas do mercado entre jogadores */
  | { t: 'market'; offers: MarketOffer[] }
  | { t: 'marketHistory'; list: MarketHistoryRow[] }
  | { t: 'houses'; list: import('./game/houses.js').HouseInfo[] }
  | { t: 'decor'; list: number[][] }
  | { t: 'marketStats'; stats: MarketStats }
  /** cena da história (id da quest; "-boss" quando o boss aparece, "-end" ao concluir) */
  | { t: 'cutscene'; id: string }
  /** conversa com um NPC (text com parágrafos separados por \n; options vazias = só fechar) */
  | { t: 'npc'; id: string; name: string; text: string; options: Array<{ id: string; label: string }> }
  /** Coloca o jogador num mapa (ao entrar no jogo, viajar ou ser corrigido). */
  | { t: 'world'; map: string; x: number; y: number; f: number; dir: number }
  /** Correção de posição dentro do mesmo mapa (passo recusado, escada). */
  | { t: 'pos'; x: number; y: number; f: number; dir: number }
  | { t: 'creatures'; list: Creature[] }
  | { t: 'creature'; c: Creature }
  | { t: 'creatureMove'; id: string; x: number; y: number; f: number; dir: number }
  | { t: 'creatureGone'; id: string }
  /** O jogador pisou num portal da cidade: o navegador abre a escolha de hunts. */
  | { t: 'portal'; kind: 'hunts' }
  | { t: 'stats'; health: number; maxHealth: number; mana: number; maxMana: number; level: number; experience: number; capacity: number; speed: number; coins?: number; promotion?: number; resets?: number; maxLevel?: number; harmony?: number; serene?: boolean;
      bank: number; vip: boolean; blessings: number; food: number; huntSellAt: number; huntProgress: Record<string, number[]> }
  | { t: 'helper'; settings: HelperSettings }
  /** Número que sobe da criatura (dano, cura, experiência). */
  | { t: 'hit'; id: string; value: number; color: number }
  /** Efeitos visuais: projéteis [x0,y0,x1,y1,id] e efeitos [x,y,id] num andar. */
  | { t: 'fx'; f: number; shots?: number[][]; effects?: number[][] }
  | { t: 'log'; channel: 'server' | 'loot' | 'death'; text: string; color?: string }
  | { t: 'chat'; msg: import('./game/chat.js').ChatMessage }
  | { t: 'chatHistory'; list: import('./game/chat.js').ChatMessage[] }
  | { t: 'analyser'; data: HuntAnalyser }
  | { t: 'auto'; on: boolean }
  | { t: 'target'; id: string | null }
  | { t: 'skills'; skills: Record<SkillId, number>; magicLevel: number;
      /** progresso (0-99%) até o próximo nível de cada skill e do magic level */
      progress?: Partial<Record<SkillId | 'magic', number>>;
      /** atributos especiais (afixos): crítico, leech */
      special?: { crit: number; critdmg: number; lifeleech: number; manaleech: number } }
  /** Resultado da caça offline, mostrado ao entrar. */
  | { t: 'offline'; report: OfflineReport }
  | { t: 'error'; error: string };

export interface OfflineReport {
  hunt: string; seconds: number; xp: number; levelBefore: number; levelAfter: number;
  kills: Array<{ name: string; count: number }>;
  drops: Array<{ id: number; name: string; count: number; rarity: number }>;
  gold: number;
}

export interface HuntAnalyser {
  hunt: string; floor: number; seconds: number;
  xp: number; xpHour: number; kills: number; loot: number; waste: number;
  /** quanto do loot foi gold (o resto é o valor de venda dos itens) */
  lootGold?: number;
  drops: Array<{ name: string; rarity: number; count: number }>;
  bestiary: Array<{ name: string; kills: number }>;
}

/** Outfit inicial: Citizen, como no Tibia. */
export function defaultOutfit(sex: Sex): Outfit {
  return { lookType: sex === 'male' ? 128 : 136, head: 78, body: 69, legs: 58, feet: 76, addons: 0 };
}
